import * as yup from 'yup';
import { toTimeInputValue } from '../../library/dateTimeHelper';
import { ClinicProgramDraft, IProgramDay, isValidProgramDay } from '../../library/models/clinicProgram';
import { TranslateFn } from '../../library/validationRules';
import ResourcesIds from '../../resources/ResourcesIds';

/** The week, wrapped in an object because a form's values have to have a root. */
export interface ProgramWeekFormValues {
    days: ClinicProgramDraft;
}

/**
 * The rules a week of opening hours has to satisfy, for the clinic's own and for a
 * therapist's alike.
 *
 * Seven rows, fixed length -- so plain indexed names rather than `useFieldArray`, which
 * exists for lists somebody can add to.
 *
 * The base rule is `isValidProgramDay`: it is already the API's rule ported once, and its
 * grain is the row, which is the grain the panel marks at. Hanging every message off
 * `closesAt` puts it where the panel already shows one, and keeps the promise that only the
 * row that is wrong is marked -- seven red fields must never stand in for one mistyped hour.
 *
 * Shut days are checked too, because the API checks them: a closed Saturday still stores
 * real hours, and storing nonsense would hand it back the next time somebody reopened it.
 *
 * `bounds` is the clinic's own week, passed only by the therapist's editor. With it, a day
 * open outside the clinic's hours -- or open at all on a day the clinic is shut -- fails
 * here rather than at the API, which refuses the same thing with the same reasoning. The
 * cap is the API's rule and this is its second statement, not its only one: the server
 * refuses an over-wide week whatever the browser thought, and narrows a stored one when the
 * clinic's hours later shrink.
 */
export const createProgramWeekSchema = (translate: TranslateFn, bounds?: IProgramDay[]) =>
    yup.object({
        days: yup.array().of(
            yup.object({
                dayOfWeek: yup.string().defined(),
                open: yup.boolean().defined(),
                opensAt: yup.string().defined(),
                closesAt: yup.string().defined()
                    .test('hours', translate(ResourcesIds.CLINIC_WORKING_HOURS_ORDER_ERROR),
                        function (value): boolean | yup.ValidationError {
                            const day: IProgramDay = {
                                ...(this.parent as IProgramDay),
                                closesAt: value ?? '',
                            };

                            if (!isValidProgramDay(day)) {
                                return false;
                            }

                            // A day nobody works is exempt from the cap; there is nothing
                            // to cap, and the API stores its hours untouched for the same
                            // reason.
                            if (!bounds || !day.open) {
                                return true;
                            }

                            const cap: IProgramDay | undefined =
                                bounds.find(entry => entry.dayOfWeek === day.dayOfWeek);

                            if (!cap || !cap.open) {
                                return this.createError({
                                    message: translate(ResourcesIds.MY_SCHEDULE_CLINIC_CLOSED_ERROR),
                                });
                            }

                            const opensAt: string = toTimeInputValue(cap.opensAt);
                            const closesAt: string = toTimeInputValue(cap.closesAt);

                            // Plain string comparison is exactly a time comparison for
                            // zero-padded 24h values, the same reason `isValidProgramDay`
                            // gives for its own ordering test.
                            if (day.opensAt < opensAt || day.closesAt > closesAt) {
                                return this.createError({
                                    message: translate(
                                        ResourcesIds.MY_SCHEDULE_OUTSIDE_CLINIC_ERROR,
                                        { opensAt, closesAt }
                                    ),
                                });
                            }

                            return true;
                        }),
            }).defined()
        ).defined(),
    });
